import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatRupiah } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { ArrowLeft, QrCode, Check, Ticket, Phone } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, totalCount, tableNumber, clear } = useCart();
  const [settings, setSettings] = useState(null);
  const [openStatus, setOpenStatus] = useState({ open: true });
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discount, type, value}
  const [promoChecking, setPromoChecking] = useState(false);
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/open-status")]).then(([s, o]) => {
      setSettings(s.data);
      setOpenStatus(o.data);
    });
  }, []);

  useEffect(() => {
    if (totalCount === 0 && !order) navigate("/");
  }, [totalCount, order, navigate]);

  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/promos/validate", { code, subtotal });
      setAppliedPromo(data);
      toast.success(`Promo ${data.code} berhasil diterapkan`);
    } catch (e) {
      setAppliedPromo(null);
      toast.error(e.response?.data?.detail || "Promo tidak berlaku");
    } finally { setPromoChecking(false); }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
  };

  const discount = appliedPromo?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  const createOrder = async () => {
    if (!tableNumber) return toast.error("Masukkan nomor meja terlebih dahulu");
    if (!openStatus.open) return toast.error(openStatus.next_message || "Toko sedang tutup");
    setSubmitting(true);
    try {
      const payload = {
        table_number: tableNumber,
        customer_name: customerName,
        customer_phone: customerPhone,
        promo_code: appliedPromo?.code || "",
        items: items.map((it) => ({
          menu_item_id: it.menu_item_id,
          name: it.name,
          unit_price: it.unit_price,
          quantity: it.quantity,
          options: it.options || [],
          notes: it.notes || "",
        })),
      };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
    } catch { toast.error("Gagal membuat pesanan"); }
    finally { setSubmitting(false); }
  };

  const confirmPayment = async () => {
    if (!order) return;
    setPaying(true);
    try {
      await api.post(`/orders/${order.id}/confirm-payment`);
      clear();
      navigate(`/success/${order.id}`);
    } catch { toast.error("Gagal konfirmasi pembayaran"); }
    finally { setPaying(false); }
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F9F9F6] pb-28">
        <header className="sticky top-0 z-20 bg-white border-b border-[#EAE7DF] px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="btn-checkout-back" className="p-2"><ArrowLeft className="w-5 h-5 text-[#1E3A2B]" /></button>
          <div className="font-display text-lg font-semibold text-[#1E3A2B]">Konfirmasi Pesanan</div>
        </header>

        {!openStatus.open && (
          <div className="bg-[#D64045] text-white text-center py-2.5 text-sm" data-testid="closed-banner-checkout">
            {openStatus.next_message || "Tutup — pesan besok"}
          </div>
        )}

        <div className="max-w-xl mx-auto p-4 space-y-3">
          <div className="bg-white rounded-2xl border border-[#EAE7DF] p-4 space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama (opsional)</span>
              <input data-testid="input-customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nama Anda" className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">No. WhatsApp (opsional)</span>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6A61]" />
                <input data-testid="input-customer-phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="08xxxxxxx (untuk struk & promo)" className="w-full border border-[#D3CEC4] rounded-xl pl-10 pr-3 py-2.5" />
              </div>
            </label>
            <div className="text-xs text-[#5C6A61]">Meja: <span className="font-semibold text-[#1E3A2B]">{tableNumber || "-"}</span></div>
          </div>

          <div className="bg-white rounded-2xl border border-[#EAE7DF] divide-y divide-[#EAE7DF]">
            {items.map((it) => {
              const optSum = (it.options || []).reduce((a, o) => a + o.price_delta, 0);
              return (
                <div key={it.cart_id} className="p-4 flex items-start justify-between text-sm">
                  <div>
                    <div className="text-[#1E3A2B]">{it.quantity}× {it.name}</div>
                    {it.options?.length > 0 && <div className="text-xs text-[#5C6A61]">{it.options.map((o) => o.name).join(", ")}</div>}
                    {it.notes && <div className="text-xs text-[#C25934] italic">{it.notes}</div>}
                  </div>
                  <div className="text-[#1E3A2B]">{formatRupiah((it.unit_price + optSum) * it.quantity)}</div>
                </div>
              );
            })}
          </div>

          {/* Promo code */}
          <div className="bg-white rounded-2xl border border-[#EAE7DF] p-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#5C6A61] mb-2 flex items-center gap-1"><Ticket className="w-3 h-3" /> Kode Promo</div>
            {!appliedPromo ? (
              <div className="flex gap-2">
                <input
                  data-testid="input-promo-code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Masukkan kode"
                  className="flex-1 border border-[#D3CEC4] rounded-xl px-3 py-2.5 uppercase tracking-wide"
                />
                <button
                  data-testid="btn-apply-promo"
                  onClick={applyPromo}
                  disabled={promoChecking || !promoCode.trim()}
                  className="bg-[#1E3A2B] text-white px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50"
                >
                  {promoChecking ? "Cek..." : "Pakai"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-[#3A7D44]/10 rounded-xl px-3 py-2.5" data-testid="applied-promo">
                <div className="text-sm">
                  <span className="font-semibold text-[#3A7D44]">{appliedPromo.code}</span>
                  <span className="text-[#5C6A61]"> · hemat {formatRupiah(appliedPromo.discount)}</span>
                </div>
                <button data-testid="btn-remove-promo" onClick={removePromo} className="text-xs text-[#D64045]">Hapus</button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#EAE7DF] p-4 space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#5C6A61]">Subtotal</span>
              <span className="text-[#1E3A2B]">{formatRupiah(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#3A7D44]">Diskon ({appliedPromo.code})</span>
                <span className="text-[#3A7D44]">-{formatRupiah(discount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-[#EAE7DF]">
              <span className="font-display text-[#1E3A2B] font-semibold">Total</span>
              <span className="font-display text-xl font-semibold text-[#C25934]" data-testid="checkout-total">{formatRupiah(total)}</span>
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#EAE7DF] p-4">
          <button
            onClick={createOrder}
            disabled={submitting || !openStatus.open}
            data-testid="btn-place-order"
            className="w-full max-w-xl mx-auto block bg-[#C25934] text-white hover:bg-[#A94726] py-3.5 rounded-2xl font-medium disabled:opacity-50"
          >
            {!openStatus.open ? "Toko Sedang Tutup" : (submitting ? "Membuat pesanan..." : "Buat Pesanan & Bayar")}
          </button>
        </div>
      </div>
    );
  }

  // Payment screen
  return (
    <div className="min-h-screen bg-[#F9F9F6] pb-28">
      <header className="sticky top-0 z-20 bg-white border-b border-[#EAE7DF] px-4 py-3 flex items-center gap-3">
        <div className="font-display text-lg font-semibold text-[#1E3A2B]">Pembayaran QRIS</div>
      </header>

      <div className="max-w-xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-2xl border border-[#EAE7DF] p-6 text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Pesanan</div>
          <div className="font-display text-2xl font-semibold text-[#1E3A2B]" data-testid="order-no">#{order.order_no}</div>
          <div className="text-sm text-[#5C6A61] mt-1">Meja {order.table_number}</div>

          <div className="my-5">
            <div className="font-display text-3xl font-semibold text-[#C25934]" data-testid="pay-amount">{formatRupiah(order.total)}</div>
            {order.discount > 0 && <div className="text-xs text-[#3A7D44] mt-1">Hemat {formatRupiah(order.discount)} dengan {order.promo?.code}</div>}
            <div className="text-xs text-[#5C6A61] mt-1">Scan QRIS di bawah untuk membayar</div>
          </div>

          <div className="w-full aspect-square max-w-xs mx-auto bg-[#EAE7DF] rounded-2xl overflow-hidden flex items-center justify-center">
            {settings?.qris_path ? (
              <img src={fileUrl(settings.qris_path)} alt="QRIS" className="w-full h-full object-contain" data-testid="qris-image" />
            ) : (
              <div className="text-center p-6">
                <QrCode className="w-14 h-14 text-[#5C6A61]/40 mx-auto mb-2" />
                <div className="text-xs text-[#5C6A61]">Admin belum mengunggah QRIS.<br/>Hubungi kasir untuk pembayaran manual.</div>
              </div>
            )}
          </div>

          {settings?.bank_account_name && <div className="text-xs text-[#5C6A61] mt-3">Penerima: {settings.bank_account_name}</div>}

          <div className="mt-6 text-xs text-[#5C6A61] leading-relaxed">
            Setelah membayar, tekan tombol <strong>"Saya Sudah Bayar"</strong>. Kasir akan memverifikasi dan menyiapkan pesanan Anda.
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#EAE7DF] p-4">
        <button
          onClick={confirmPayment}
          disabled={paying}
          data-testid="btn-confirm-payment"
          className="w-full max-w-xl mx-auto block bg-[#3A7D44] text-white hover:bg-[#2b5f33] py-3.5 rounded-2xl font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <Check className="w-5 h-5" /> {paying ? "Memproses..." : "Saya Sudah Bayar"}
        </button>
      </div>
    </div>
  );
}
